import { api } from '../../core/network/api';

// SOFT = silver (The Tradestead) · PREMIUM = runes (The Smugglers’ Ice).
export type MarketCurrency = 'SOFT' | 'PREMIUM';
// Explicit source of truth for what's buyable — SOFT: basePrice only ·
// PREMIUM: premiumPrice only (basePrice is 0) · BOTH: either.
export type SaleCurrency = 'SOFT' | 'PREMIUM' | 'BOTH';

// ── NPC store (catalog + quick-sell) ──
export interface CatalogItem {
  id: string; name: string;
  type: string; rarity: string;
  attack: number; defense: number; magic: number; speed: number; hp: number;
  setName: string | null; statusEffect: string | null;
  basePrice: number;
  /** Account level (User.level) required to buy this — see item-expansion-14-08.md §3.4. */
  minLevel: number;
  /** The item's OWN rune price, or null if the def never set one. Prefer
   *  `runePrice` for anything the player is charged — this is only useful for
   *  telling a natively-priced item from an alt-buy one. */
  premiumPrice: number | null;
  saleCurrency: SaleCurrency;
  /**
   * What the rack charges in runes — `premiumPrice` when the item has one, else
   * dominion's alt-buy price derived from the silver price. Every listed item
   * has one, so epic and legendary are buyable "for runes and silver"
   * (QA 86cb9yd3p item 5). Always the number to display and to charge.
   */
  runePrice: number;
}

export const fetchCatalog = () => api.get<CatalogItem[]>('/market/catalog');

export const buyFromCatalog = (catalogId: string, currency?: MarketCurrency) =>
  api.post<{ bought: string; name: string; cost: number }>('/market/catalog/buy', { catalogId, currency });

export const quickSellArtifact = (artifactId: string) =>
  api.post<{ sold: string; value: number; currency: MarketCurrency; cash: number }>(
    '/market/quicksell', { artifactId });

// ── Two-tier sell: instant quicksell (50% silver / 70% runes) vs the fence (90%, 12h lock) ──
export interface SellQuote {
  gross: number;
  /** The item's OWN currency — every payout below is denominated in it, never converted. */
  currency: MarketCurrency;
  quick: { pct: number; payout: number };
  market: { pct: number; payout: number; lockHours: number };
}
export const fetchSellQuote = (artifactId: string) =>
  api.get<SellQuote>(`/market/sell-quote/${artifactId}`);

export const marketSellArtifact = (artifactId: string) =>
  api.post<{
    pendingId: string; payout: number; currency: MarketCurrency;
    matureAt: string; itemName: string;
  }>('/market/market-sell', { artifactId });

export interface PendingSaleEntry {
  id: string; itemName: string; rarity: string; type: string;
  payout: number; currency: MarketCurrency; matureAt: string;
}
export interface PendingView {
  collected: number;
  /** Runes cleared by the broker this poll (rune-priced gear pays out in runes). */
  collectedPremium: number;
  cash: number | null;
  runes: number | null;
  pending: PendingSaleEntry[];
}
/** Fetches locked payouts — server first credits any that have matured (lazy). */
export const fetchPending = () => api.get<PendingView>('/market/pending');
